import React, { useMemo, useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import styled, { keyframes } from 'styled-components'
import DetailsUnitTableItem from 'features/bpDetails/Components/units/DetailsUnitTableItem'
import Modal from 'components/atoms/Modal'
import { propOr, pathOr } from 'ramda'
import UnitForm from 'features/createBp/components/atoms/UnitForm'
import { useDispatch, useSelector } from 'react-redux'
import { removeBpUnitRoutine, updateBpUnitRoutine, fetchUnitRoutine } from 'ducks/units/actions'
import { getSelectedBp } from 'features/bpDetails/ducks/selectors'
import { selectUnitForEditForm } from 'ducks/units/selectors'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import CloseIcon from '@mui/icons-material/Close'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import MuiMenu from '@mui/material/Menu'
import MuiMenuItem from '@mui/material/MenuItem'
import { redirect } from 'utils/paths'
import { getFloorsIntervals } from 'utils/bpData'
import { isNotNilOrEmpty } from 'utils/ramda'
import useBodyScrollLock from 'hooks/useBodyScrollLock'
import UnitEpcBadge from 'features/bpDetails/Components/units/UnitEpcBadge'

const naturalCompare = (a, b) => {
  const as = String(a ?? '').trim()
  const bs = String(b ?? '').trim()
  if (as === bs) return 0

  const ax = as.match(/(\d+|\D+)/g) || [as]
  const bx = bs.match(/(\d+|\D+)/g) || [bs]

  const len = Math.max(ax.length, bx.length)
  for (let i = 0; i < len; i++) {
    const x = ax[i]
    const y = bx[i]

    if (x == null) return -1
    if (y == null) return 1
    if (x === y) continue

    const xNum = /^\d+$/.test(x)
    const yNum = /^\d+$/.test(y)

    if (xNum && yNum) {
      const xn = parseInt(x, 10)
      const yn = parseInt(y, 10)
      if (xn !== yn) return xn - yn
      if (x.length !== y.length) return x.length - y.length
      continue
    }

    const xc = x.toLowerCase()
    const yc = y.toLowerCase()
    if (xc < yc) return -1
    if (xc > yc) return 1

    if (x < y) return -1
    if (x > y) return 1
  }

  return as.toLowerCase() < bs.toLowerCase() ? -1 : 1
}

const toNumberOrNull = val => {
  if (val == null) return null
  const n = Number(val)
  return Number.isFinite(n) ? n : null
}

const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(false)

  useEffect(() => {
    const media = window.matchMedia(query)
    if (media.matches !== matches) {
      setMatches(media.matches)
    }
    const listener = () => setMatches(media.matches)
    media.addEventListener('change', listener)
    return () => media.removeEventListener('change', listener)
  }, [matches, query])

  return matches
}

// Helper function for floor numbers
const formatFloorNumbers = floors => {
  if (!isNotNilOrEmpty(floors)) return '---'
  const sorted = floors
    .map(num => num === 'lower_ground' ? -0.5 : Number(num))
    .sort((a, b) => b - a)
  return getFloorsIntervals(sorted).join(', ')
}

const MobileUnitCard = ({
  unit,
  index,
  redirectPath,
  hasFullAccess,
  isSoleUnit,
  isSoleResidential,
  unitsCount,
  openEditModal,
  openDeleteModal,
  bpUnits
}) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const dropdownOpen = Boolean(anchorEl)

  const openMenu = e => {
    e.stopPropagation()
    e.preventDefault()
    setAnchorEl(e.currentTarget)
  }

  const handleCloseMenu = () => {
    setAnchorEl(null)
  }

  const handleCardClick = () => {
    if (!anchorEl) {
      redirect(`${redirectPath}/${unit.id}`)
    }
  }

  const handleEdit = e => {
    handleCloseMenu()
    openEditModal(unit)(e)
  }

  const handleDelete = e => {
    handleCloseMenu()
    openDeleteModal(unit)(e)
  }

  const ownerName = pathOr(false, ['owners', 0], unit)
    ? `${unit.owners[0].firstName} ${unit.owners[0].lastName}`
    : 'Unassigned'

  return (
    <UnitCard onClick={handleCardClick} delay={index * 0.03}>
      <CardHeader>
        <UnitName>{unit.name || 'Unnamed Unit'}</UnitName>
        {hasFullAccess && (
          <MenuButton onClick={openMenu}>
            <MoreVertIcon />
          </MenuButton>
        )}
        <MuiMenu
          keepMounted
          anchorEl={anchorEl}
          open={dropdownOpen}
          onClose={handleCloseMenu}
          disablePortal={false}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right'
          }}
          sx={{ zIndex: 20000 }}
          PaperProps={{
            style: {
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              minWidth: '140px'
            },
            sx: {
              zIndex: 20001
            }
          }}
        >
          <MuiMenuItem
            onClick={handleEdit}
            disabled={isSoleResidential || (unitsCount === 1 && isSoleUnit)}
            style={{ fontSize: '14px', padding: '10px 16px' }}
          >
            Edit
          </MuiMenuItem>
          <MuiMenuItem
            onClick={handleDelete}
            disabled={bpUnits.length <= 1 || isSoleResidential}
            style={{ fontSize: '14px', padding: '10px 16px', color: '#ef4444' }}
          >
            Remove Unit
          </MuiMenuItem>
        </MuiMenu>
      </CardHeader>

      <CardBody>
        <CardRow>
          <CardLabel>UPRN</CardLabel>
          <CardValue>{unit.uprn || 'Not yet allocated'}</CardValue>
        </CardRow>
        <CardRow>
          <CardLabel>Plot</CardLabel>
          <CardValue>{unit.plotNumber || '---'}</CardValue>
        </CardRow>
        <CardRow>
          <CardLabel>Floors</CardLabel>
          <CardValue>{formatFloorNumbers(unit.floorNumbers)}</CardValue>
        </CardRow>
        <CardRow>
          <CardLabel>Area (sqm)</CardLabel>
          <CardValue>{unit.area || '---'}</CardValue>
        </CardRow>
        <CardRow>
          <CardLabel>EPC</CardLabel>
          <CardValue><UnitEpcBadge unit={unit} compact /></CardValue>
        </CardRow>
        <CardRow>
          <CardLabel>Owner</CardLabel>
          <CardValue>{ownerName}</CardValue>
        </CardRow>
      </CardBody>

      <CardFooter>
        <ViewPrompt>Tap to view details →</ViewPrompt>
      </CardFooter>
    </UnitCard>
  )
}

const DetailsUnitTable = ({ units, section, isSoleUnit, isSoleResidential, redirectPath, onRequestAddUnits, editMode = false }) => {
  const bp = useSelector(getSelectedBp)
  const editFormUnit = useSelector(selectUnitForEditForm)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [selectedUnit, setSelectedUnit] = useState(null)
  const [selectedUnitIndex, setSelectedUnitIndex] = useState(-1)
  const [closeDropdownTrigger, setCloseDropdownTrigger] = useState(0)
  const [scrollYBeforeEdit, setScrollYBeforeEdit] = useState(null)
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' })
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [transitionLabel, setTransitionLabel] = useState('Saving…')
  const [pendingNextUnitId, setPendingNextUnitId] = useState(null)
  const [frozenOrder, setFrozenOrder] = useState(null)
  const dispatch = useDispatch()
  const hasFullAccess = bp.hasAccessToBuildingPassport

  const isMobile = useMediaQuery('(max-width: 480px)')
  const isTablet = useMediaQuery('(max-width: 768px)')

  useBodyScrollLock(editModalOpen && (isMobile || isTablet))
  useBodyScrollLock(deleteModalOpen && (isMobile || isTablet))

  const requestSort = key => {
    setSortConfig(prev => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
      }
      return { key, direction: 'asc' }
    })
  }

  const getSortValue = (unit, key) => {
    switch (key) {
      case 'name':
        return (unit?.name ?? '').toString()
      case 'uprn':
        return (unit?.uprn ?? unit?.UPRN ?? '').toString()
      case 'plotNumber':
        return (unit?.plotNumber ?? unit?.plot_number ?? '').toString()
      case 'floors': {
        const v =
          unit?.floors ??
          unit?.floor ??
          unit?.floor_count ??
          unit?.floorsCount ??
          unit?.floors_count
        const n = toNumberOrNull(v)
        return n != null ? n : (v ?? '').toString()
      }
      case 'area': {
        const v =
          unit?.area ??
          unit?.areaSqm ??
          unit?.area_sqm ??
          unit?.areaInSqm ??
          unit?.area_in_sqm
        const n = toNumberOrNull(v)
        return n != null ? n : (v ?? '').toString()
      }
      case 'owner': {
        const v =
          unit?.ownerName ??
          unit?.owner?.name ??
          unit?.owner ??
          unit?.assignedOwner ??
          ''
        return (v ?? '').toString()
      }
      default:
        return ''
    }
  }

  const sortedUnits = useMemo(() => {
    if (!units) return []
    const copy = [ ...units ]
    const { key, direction } = sortConfig

    copy.sort((u1, u2) => {
      const v1 = getSortValue(u1, key)
      const v2 = getSortValue(u2, key)

      // Empty values last
      const v1Empty = v1 === '' || v1 == null
      const v2Empty = v2 === '' || v2 == null
      if (v1Empty && !v2Empty) return 1
      if (!v1Empty && v2Empty) return -1

      const bothNumbers = typeof v1 === 'number' && typeof v2 === 'number'
      let cmp = 0

      if (bothNumbers) {
        cmp = v1 - v2
      } else {
        cmp = naturalCompare(v1, v2)
      }

      if (cmp === 0) {
        cmp = naturalCompare(u1?.id ?? '', u2?.id ?? '')
      }

      return direction === 'asc' ? cmp : -cmp
    })

    return copy
  }, [units, sortConfig])

  // While editing inline, renaming a unit must not make its row jump to a new
  // sorted position under the cursor. Freeze the order when edit mode starts
  // (or the sort changes) and only append units created in the meantime.
  useEffect(() => {
    if (!editMode) {
      setFrozenOrder(null)
      return
    }
    setFrozenOrder(sortedUnits.map(unit => unit.id))
  }, [editMode, sortConfig])

  const displayedUnits = useMemo(() => {
    if (!editMode || !frozenOrder) return sortedUnits
    const position = id => {
      const index = frozenOrder.indexOf(id)
      return index === -1 ? Number.MAX_SAFE_INTEGER : index
    }
    return [...sortedUnits].sort((a, b) => position(a.id) - position(b.id))
  }, [sortedUnits, editMode, frozenOrder])

  const restoreScrollPosition = () => {
    if (scrollYBeforeEdit == null) return
    setTimeout(() => {
      try {
        window.scrollTo(0, scrollYBeforeEdit)
      } catch (err) {}
    }, 0)
  }

  const handleEditModalOpen = unit => e => {
    e?.stopPropagation?.()
    e?.preventDefault?.()

    try {
      setScrollYBeforeEdit(window.scrollY)
    } catch (err) {
      setScrollYBeforeEdit(null)
    }

    const idx = sortedUnits.findIndex(u => u?.id === unit?.id)
    setSelectedUnit(unit)
    setSelectedUnitIndex(idx)
    setEditModalOpen(true)
  }

  const handleEditModalClose = () => {
    setIsTransitioning(false)
    setTransitionLabel('Saving…')
    setPendingNextUnitId(null)

    setSelectedUnit(null)
    setSelectedUnitIndex(-1)
    setEditModalOpen(false)
    setCloseDropdownTrigger(Math.random())
    restoreScrollPosition()
  }

  const handleDeleteModalOpen = unit => e => {
    e?.stopPropagation?.()
    e?.preventDefault?.()
    setSelectedUnit(unit)
    setDeleteModalOpen(true)
  }

  const handleDeleteModalClose = () => {
    setSelectedUnit(null)
    setDeleteModalOpen(false)
    setCloseDropdownTrigger(Math.random())
  }

  const handleRemoveUnit = values => () => {
    dispatch(removeBpUnitRoutine(values))
    handleDeleteModalClose()
  }

  const handleUpdateUnit = values => {
    const mode = values?.__afterSave // 'save' | 'saveNext'

    if (mode === 'saveNext') {
      setIsTransitioning(true)
      setTransitionLabel('Saving…')
    }

    dispatch(updateBpUnitRoutine({
      ...values,
      bpId: bp.id
    }))
  }

  const goPrevUnit = () => {
    if (!sortedUnits?.length) return
    if (selectedUnitIndex <= 0) return

    const nextIndex = selectedUnitIndex - 1
    const nextUnit = sortedUnits[nextIndex]
    if (!nextUnit) return

    setPendingNextUnitId(nextUnit.id)
    setIsTransitioning(true)
    setTransitionLabel('Loading…')

    setSelectedUnit(nextUnit)
    setSelectedUnitIndex(nextIndex)

    dispatch(fetchUnitRoutine({
      bpId: bp.id,
      unitId: nextUnit.id,
      editForm: true
    }))
  }

  const goNextUnit = () => {
    if (!sortedUnits?.length) return
    if (selectedUnitIndex >= sortedUnits.length - 1) return

    const nextIndex = selectedUnitIndex + 1
    const nextUnit = sortedUnits[nextIndex]
    if (!nextUnit) return

    setPendingNextUnitId(nextUnit.id)
    setIsTransitioning(true)
    setTransitionLabel('Loading…')

    setSelectedUnit(nextUnit)
    setSelectedUnitIndex(nextIndex)

    dispatch(fetchUnitRoutine({
      bpId: bp.id,
      unitId: nextUnit.id,
      editForm: true
    }))
  }

  const canGoPrev = selectedUnitIndex > 0
  const canGoNext = selectedUnitIndex >= 0 && selectedUnitIndex < (sortedUnits.length - 1)
  const isLastUnit = selectedUnitIndex >= 0 && selectedUnitIndex === (sortedUnits.length - 1)

  const sortIndicator = key => {
    if (sortConfig.key !== key) return null
    return sortConfig.direction === 'asc' ? '▲' : '▼'
  }

  const handleSaveAndNextSuccess = () => {
    setTransitionLabel('Loading next…')

    const nextIndex = selectedUnitIndex + 1
    const nextUnit = sortedUnits[nextIndex]

    if (!nextUnit) {
      setIsTransitioning(false)
      setTransitionLabel('Saving…')
      setPendingNextUnitId(null)
      if (typeof onRequestAddUnits === 'function') {
        handleEditModalClose()
        setTimeout(() => onRequestAddUnits(), 0)
      }
      return
    }

    setPendingNextUnitId(nextUnit.id)
    setSelectedUnit(nextUnit)
    setSelectedUnitIndex(nextIndex)

    dispatch(fetchUnitRoutine({
      bpId: bp.id,
      unitId: nextUnit.id,
      editForm: true
    }))
  }

  useEffect(() => {
    if (!isTransitioning) return
    if (!pendingNextUnitId) return

    const loadedId = editFormUnit?.id
    if (loadedId !== pendingNextUnitId) return

    setIsTransitioning(false)
    setTransitionLabel('Saving…')
    setPendingNextUnitId(null)
  }, [isTransitioning, pendingNextUnitId, editFormUnit])

  const modalRoot = typeof document !== 'undefined' ? document.body : null

  if (isMobile || isTablet) {
    return (
      <>
        <CardsContainer>
          {sortedUnits.map((unit, index) => (
            <MobileUnitCard
              key={`unit-card-${unit.id}`}
              unit={unit}
              index={index}
              redirectPath={redirectPath}
              hasFullAccess={hasFullAccess}
              isSoleUnit={isSoleUnit}
              isSoleResidential={isSoleResidential}
              unitsCount={sortedUnits.length}
              openEditModal={handleEditModalOpen}
              openDeleteModal={handleDeleteModalOpen}
              bpUnits={sortedUnits}
            />
          ))}
        </CardsContainer>

        {modalRoot && createPortal(
          <MobileModal isOpen={editModalOpen}>
            <MobileModalOverlay onClick={handleEditModalClose} />
            <MobileModalContent>
              <MobileModalHeader>
                <MobileModalTitle>Edit Unit</MobileModalTitle>
                <MobileCloseButton onClick={handleEditModalClose}>
                  <CloseIcon />
                </MobileCloseButton>
              </MobileModalHeader>

              <MobileNavBar>
                <NavButtonMobile
                  onClick={goPrevUnit}
                  disabled={!canGoPrev || isTransitioning}
                >
                  <ArrowBackIosNewIcon />
                  <span>Prev</span>
                </NavButtonMobile>

                <NavCounter>
                  {selectedUnitIndex >= 0 && sortedUnits?.length
                    ? `${selectedUnitIndex + 1} / ${sortedUnits.length}`
                    : ''}
                </NavCounter>

                <NavButtonMobile
                  onClick={goNextUnit}
                  disabled={!canGoNext || isTransitioning}
                >
                  <span>Next</span>
                  <ArrowForwardIosIcon />
                </NavButtonMobile>
              </MobileNavBar>

              {isTransitioning && (
                <TransitionOverlay>
                  <Spinner />
                  <TransitionText>{transitionLabel}</TransitionText>
                </TransitionOverlay>
              )}

              <MobileFormContainer>
                <UnitForm
                  key={selectedUnit?.id || 'unit-form'}
                  unit={selectedUnit}
                  closeEditModal={handleEditModalClose}
                  onSubmit={handleUpdateUnit}
                  onSaveAndNextSuccess={handleSaveAndNextSuccess}
                  disabledActions={isTransitioning}
                  bp={bp}
                  buildingType={section.buildingType}
                  hideAssignments
                  saveNextLabel={isLastUnit ? 'Save and create another unit' : 'Save & Next'}
                />
              </MobileFormContainer>
            </MobileModalContent>
          </MobileModal>,
          modalRoot
        )}

        {modalRoot && createPortal(
          <MobileModal isOpen={deleteModalOpen}>
            <MobileModalOverlay onClick={handleDeleteModalClose} />
            <DeleteModalContent>
              <DeleteIcon>
                <WarningAmberIcon />
              </DeleteIcon>
              <DeleteTitle>Delete Unit?</DeleteTitle>
              <DeleteMessage>
                Removing <strong>{propOr('', 'name', selectedUnit)}</strong> will delete this unit
                and remove any links made to files within the Building Passport.
                Any files specific to this unit will also be deleted.
              </DeleteMessage>
              <DeleteActions>
                <CancelButton onClick={handleDeleteModalClose}>
                  Cancel
                </CancelButton>
                <ConfirmDeleteButton
                  onClick={handleRemoveUnit({ bpId: bp.id, unitId: propOr('', 'id', selectedUnit) })}
                >
                  Delete Unit
                </ConfirmDeleteButton>
              </DeleteActions>
            </DeleteModalContent>
          </MobileModal>,
          modalRoot
        )}
      </>
    )
  }

  return (
    <>
      <TableWrapper>
        <thead>
        <tr>
          <SortableTh onClick={() => requestSort('name')} isActive={sortConfig.key === 'name'}>
            Unit name <SortMark isActive={sortConfig.key === 'name'}>{sortIndicator('name')}</SortMark>
          </SortableTh>
          <SortableTh onClick={() => requestSort('uprn')} isActive={sortConfig.key === 'uprn'}>
            UPRN <SortMark isActive={sortConfig.key === 'uprn'}>{sortIndicator('uprn')}</SortMark>
          </SortableTh>
          <SortableTh onClick={() => requestSort('plotNumber')} isActive={sortConfig.key === 'plotNumber'}>
            Plot <SortMark isActive={sortConfig.key === 'plotNumber'}>{sortIndicator('plotNumber')}</SortMark>
          </SortableTh>
          <SortableTh onClick={() => requestSort('floors')} isActive={sortConfig.key === 'floors'}>
            Floor(s) <SortMark isActive={sortConfig.key === 'floors'}>{sortIndicator('floors')}</SortMark>
          </SortableTh>
          <SortableTh onClick={() => requestSort('area')} isActive={sortConfig.key === 'area'}>
            Area (sqm) <SortMark isActive={sortConfig.key === 'area'}>{sortIndicator('area')}</SortMark>
          </SortableTh>
          <th>EPC</th>
          <SortableTh onClick={() => requestSort('owner')} isActive={sortConfig.key === 'owner'}>
            Owner <SortMark isActive={sortConfig.key === 'owner'}>{sortIndicator('owner')}</SortMark>
          </SortableTh>
          <th />
        </tr>
        </thead>

        <tbody>
        {displayedUnits.map(unit => (
          <DetailsUnitTableItem
            redirectPath={redirectPath}
            key={`unit-${unit.id}`}
            unit={unit}
            editMode={editMode}
            openEditModal={handleEditModalOpen}
            openDeleteModal={handleDeleteModalOpen}
            closeDropdownTrigger={closeDropdownTrigger}
            isSoleUnit={isSoleUnit}
            isSoleResidential={isSoleResidential}
            unitsCount={sortedUnits.length}
            hasFullAccess={hasFullAccess}
          />
        ))}
        </tbody>
      </TableWrapper>

      <Modal
        open={editModalOpen}
        title='Edit unit'
        onClose={handleEditModalClose}
      >
        <EditModalBody>
          <EditNavBar>
            <EditNavLeft>
              <NavButton type='button' onClick={goPrevUnit} disabled={!canGoPrev || isTransitioning}>
                Previous
              </NavButton>
              <NavButton type='button' onClick={goNextUnit} disabled={!canGoNext || isTransitioning}>
                Next
              </NavButton>
            </EditNavLeft>

            <EditNavRight>
              {selectedUnitIndex >= 0 && sortedUnits?.length
                ? (
                  <EditCounter>
                    {selectedUnitIndex + 1} / {sortedUnits.length}
                  </EditCounter>
                )
                : null}
            </EditNavRight>
          </EditNavBar>

          {isTransitioning && (
            <BlockingOverlay>
              <OverlayCard>
                <OverlaySpinner />
                <OverlayText>{transitionLabel}</OverlayText>
              </OverlayCard>
            </BlockingOverlay>
          )}

          <UnitForm
            key={selectedUnit?.id || 'unit-form'}
            unit={selectedUnit}
            closeEditModal={handleEditModalClose}
            onSubmit={handleUpdateUnit}
            onSaveAndNextSuccess={handleSaveAndNextSuccess}
            disabledActions={isTransitioning}
            bp={bp}
            buildingType={section.buildingType}
            hideAssignments
            saveNextLabel={isLastUnit ? 'Save and create another unit' : 'Save & Next'}
          />
        </EditModalBody>
      </Modal>

      <Modal
        open={deleteModalOpen}
        title='Delete unit'
        onClose={handleDeleteModalClose}
        withCancel
        onCancel={handleDeleteModalClose}
        withSubmit
        onSubmit={handleRemoveUnit({ bpId: bp.id, unitId: propOr('', 'id', selectedUnit) })}
      >
        Removing <ModalUnitName>{propOr('', 'name', selectedUnit)}</ModalUnitName> will delete this unit
        and remove any links that were made to files within the Building Passport.
        Any files specific to this unit will also be deleted. Are you sure you wish to continue?
      </Modal>
    </>
  )
}

export default DetailsUnitTable

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(100%);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`

const breakpoints = {
  mobile: '480px',
  tablet: '768px',
  desktop: '1024px'
}

const colors = {
  darkNavy: '#0c132b',
  teal: '#36becd',
  lightBg: '#fcfbfe',
  lightBg2: '#f6f7fe',
  lightBg3: '#eef2fa',
  lightBg4: '#e8eaf5',
  lightBg5: '#cdd4ea',
  lightTeal: '#bbeaf0',
  orange: '#ffaf7f',
  orangeDark: '#fd836b',
  red: '#cd2000',
  yellow: '#ffc111'
}

const CardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;

  @media (max-width: ${breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`

const UnitCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: 18px;
  box-shadow: 0 1px 3px rgba(12, 19, 43, 0.04), 0 4px 12px rgba(12, 19, 43, 0.03);
  border: 1px solid ${colors.lightBg4};
  cursor: pointer;
  transition: all 0.25s ease;
  animation: ${fadeIn} 0.4s ease-out ${({ delay }) => delay}s both;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(12, 19, 43, 0.08), 0 8px 24px rgba(12, 19, 43, 0.06);
    border-color: ${colors.lightTeal};
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: ${breakpoints.mobile}) {
    padding: 16px;
    border-radius: 8px;
  }
`

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 14px;
  padding-bottom: 12px;
  border-bottom: 1px solid ${colors.lightBg3};
`

const UnitName = styled.h4`
  font-family: -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 17px;
  font-weight: 700;
  color: ${colors.darkNavy};
  margin: 0;
  letter-spacing: -0.3px;
  flex: 1;
`

const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const CardRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const CardLabel = styled.span`
  font-family: -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 13px;
  color: ${colors.teal};
  font-weight: 500;
`

const CardValue = styled.span`
  font-family: -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 14px;
  color: ${colors.darkNavy};
  font-weight: 600;
  text-align: right;
`

const CardFooter = styled.div`
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid ${colors.lightBg3};
  text-align: center;
`

const ViewPrompt = styled.span`
  font-family: -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 12px;
  color: ${colors.teal};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const MenuButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin: -8px -8px -8px 0;

  svg {
    font-size: 20px;
    color: ${colors.red};
  }

  &:hover {
    background: ${colors.lightBg3};
  }

  &:active {
    background: ${colors.lightBg4};
  }
`

// Mobile Modal Styles
const MobileModal = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: ${({ isOpen }) => isOpen ? 'flex' : 'none'};
  flex-direction: column;
  justify-content: flex-end;
`

const MobileModalOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  background: rgba(12, 19, 43, 0.5);
  backdrop-filter: blur(4px);
`

const MobileModalContent = styled.div`
  position: relative;
  z-index: 2;
  background: white;
  border-radius: 12px 12px 0 0;
  max-height: 80vh;
  padding-bottom: 50px;
  display: flex;
  flex-direction: column;
  animation: ${slideUp} 0.3s ease-out;
  overflow: hidden;
`

const MobileModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 20px 16px;
  border-bottom: 1px solid ${colors.lightBg3};
  flex-shrink: 0;
`

const MobileModalTitle = styled.h3`
  font-family: -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: ${colors.darkNavy};
  margin: 0;
`

const MobileCloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: ${colors.lightBg3};
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;

  svg {
    font-size: 20px;
    color: ${colors.darkNavy};
  }

  &:hover {
    background: ${colors.lightBg4};
  }
`

const MobileNavBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  background: ${colors.lightBg2};
  flex-shrink: 0;
`

const NavButtonMobile = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: white;
  border: 1px solid ${colors.lightBg4};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;

  span {
    font-family: -apple-system, BlinkMacSystemFont, sans-serif;
    font-size: 14px;
    font-weight: 600;
    color: ${colors.darkNavy};
  }

  svg {
    font-size: 14px;
    color: ${colors.lightBg5};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &:not(:disabled):hover {
    background: ${colors.lightBg2};
    border-color: ${colors.lightBg5};
  }
`

const NavCounter = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: ${colors.lightBg5};
`

const MobileFormContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  -webkit-overflow-scrolling: touch;
`

const TransitionOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  z-index: 10;
`

const Spinner = styled.div`
  width: 32px;
  height: 32px;
  border: 3px solid ${colors.lightBg4};
  border-top-color: ${colors.teal};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`

const TransitionText = styled.span`
  font-family: -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: ${colors.lightBg5};
`

// Delete Modal Styles
const DeleteModalContent = styled.div`
  position: relative;
  background: white;
  border-radius: 12px 12px 0 0;
  padding: 32px 24px;
  text-align: center;
  animation: ${slideUp} 0.3s ease-out;
`

const DeleteIcon = styled.div`
  width: 64px;
  height: 64px;
  margin: 0 auto 20px;
  background: ${colors.orange}30;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    font-size: 32px;
    color: ${colors.red};
  }
`

const DeleteTitle = styled.h3`
  font-family: -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 22px;
  font-weight: 700;
  color: ${colors.darkNavy};
  margin: 0 0 12px;
`

const DeleteMessage = styled.p`
  font-family: -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 15px;
  color: ${colors.lightBg5};
  line-height: 1.6;
  margin: 0 0 24px;

  strong {
    color: ${colors.darkNavy};
  }
`

const DeleteActions = styled.div`
  display: flex;
  gap: 12px;
`

const CancelButton = styled.button`
  flex: 1;
  padding: 14px;
  background: ${colors.lightBg3};
  border: none;
  border-radius: 6px;
  font-family: -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: ${colors.darkNavy};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.lightBg4};
  }
`

const ConfirmDeleteButton = styled.button`
  flex: 1;
  padding: 14px;
  background: ${colors.red};
  border: none;
  border-radius: 6px;
  font-family: -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(205, 32, 0, 0.3);

  &:hover {
    box-shadow: 0 4px 12px rgba(205, 32, 0, 0.4);
  }
`

// Desktop Table Styles
const TableWrapper = styled.table`
  width: 100%;

  th {
    box-sizing: border-box;
    color: ${colors.lightBg5};
    font-family: -apple-system, BlinkMacSystemFont, sans-serif;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    text-align: left;
    height: 44px;
    padding: 0 12px;
    position: relative;
    user-select: none;
    white-space: nowrap;
    border-bottom: 1px solid ${colors.lightBg3};
  }

  td {
    box-sizing: border-box;
    padding: 0 12px;
    height: 60px;
    font-family: -apple-system, BlinkMacSystemFont, sans-serif;
    font-size: 14px;
    color: ${colors.darkNavy};
  }

  tbody > tr {
    cursor: pointer;
    transition: all 0.2s ease;

    &:not(:last-of-type) {
      border-bottom: 1px solid ${colors.lightBg3};
    }

    &:hover {
      background: linear-gradient(135deg, ${colors.lightBg2} 0%, ${colors.lightBg3} 100%);
    }

    & > td:first-of-type {
      font-weight: 600;
      color: ${colors.darkNavy};
    }
  }
`

const SortableTh = styled.th`
  cursor: pointer;
  opacity: ${({ isActive }) => isActive ? 1 : 0.7};
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }
`

const SortMark = styled.span`
  display: inline-block;
  margin-left: 6px;
  font-size: 10px;
  opacity: ${({ isActive }) => isActive ? 1 : 0};
  transition: opacity 0.2s ease;
`

const EditModalBody = styled.div`
  position: relative;
`

const EditNavBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${colors.lightBg3};
`

const EditNavLeft = styled.div`
  display: flex;
  gap: 10px;
`

const EditNavRight = styled.div`
  display: flex;
  align-items: center;
`

const EditCounter = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${colors.lightBg5};
`

const NavButton = styled.button`
  border: 1px solid ${colors.lightBg4};
  background: white;
  padding: 8px 14px;
  border-radius: 6px;
  cursor: pointer;
  font-family: -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: ${colors.darkNavy};
  transition: all 0.2s ease;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }

  &:not(:disabled):hover {
    background: ${colors.lightBg2};
    border-color: ${colors.lightBg5};
  }
`

const BlockingOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 10;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
`

const OverlayCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  border-radius: 8px;
  background: white;
  border: 1px solid ${colors.lightBg4};
  box-shadow: 0 10px 30px rgba(12, 19, 43, 0.1);
`

const OverlayText = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: ${colors.lightBg5};
`

const OverlaySpinner = styled.div`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid ${colors.lightBg4};
  border-top-color: ${colors.teal};
  animation: ${spin} 0.8s linear infinite;
`

const ModalUnitName = styled.span`
  font-weight: 700;
  color: ${colors.darkNavy};
`
